
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Plus, RefreshCw, Eye, EyeOff } from "lucide-react"
import { useCreateStaffMember, StaffType } from "@/hooks/useStaffMembers"
import { useCondominium } from "@/hooks/useCondominium"
import { formatCPF, validateCPF } from "@/utils/cpfValidation"
import { generatePassword } from "@/utils/passwordUtils"
import PhotoCapture from "./PhotoCapture"
import { useToast } from "@/hooks/use-toast"

const NovoColaboradorModal = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    staff_type: "" as StaffType,
    cpf: "",
    workload_hours: 8,
    photo_url: "",
    password: ""
  })
  const [showPassword, setShowPassword] = useState(false)
  const [generatedPassword, setGeneratedPassword] = useState("")
  const { toast } = useToast()

  const createStaffMember = useCreateStaffMember()
  const { data: condominium } = useCondominium()
  
  const isPontoEletronico = condominium?.type === 'ponto_eletronico'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.full_name || !formData.staff_type) {
      toast({
        title: "Campos obrigatórios",
        description: "Nome completo e função são obrigatórios.",
        variant: "destructive",
      })
      return
    }

    if (formData.cpf && !validateCPF(formData.cpf)) {
      toast({
        title: "CPF inválido",
        description: "Por favor, insira um CPF válido.",
        variant: "destructive",
      })
      return
    }

    try {
      await createStaffMember.mutateAsync({
        full_name: formData.full_name,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        staff_type: formData.staff_type,
        cpf: formData.cpf || undefined,
        workload_hours: formData.workload_hours,
        photo_url: formData.photo_url || undefined,
        password: formData.password || undefined
      })
      
      setFormData({
        full_name: "",
        email: "",
        phone: "",
        staff_type: "" as StaffType,
        cpf: "",
        workload_hours: 8,
        photo_url: "",
        password: ""
      })
      setGeneratedPassword("")
      setIsOpen(false)
    } catch (error: any) {
      console.error("Erro ao criar colaborador:", error)
      toast({
        title: "Erro ao cadastrar",
        description: error?.message || "Erro desconhecido ao criar colaborador.",
        variant: "destructive",
      })
    }
  }

  const handleGeneratePassword = () => {
    const newPassword = generatePassword(10)
    setGeneratedPassword(newPassword)
    setFormData(prev => ({ ...prev, password: newPassword }))
    toast({
      title: "Senha gerada!",
      description: "Uma nova senha foi gerada automaticamente.",
    })
  }

  const handleCpfChange = (value: string) => {
    const formatted = formatCPF(value)
    setFormData(prev => ({ ...prev, cpf: formatted }))
  }

  const getStaffTypeOptions = () => {
    if (isPontoEletronico) {
      return [
        { value: "gerente" as StaffType, label: "Gerente" },
        { value: "operador" as StaffType, label: "Operador" }
      ]
    }

    if (condominium?.type === 'controle_acessos') {
      return [
        { value: "porteiro" as StaffType, label: "Porteiro" },
        { value: "administracao" as StaffType, label: "Administração" }
      ]
    }
    
    return [
      { value: "porteiro" as StaffType, label: "Porteiro" },
      { value: "zelador" as StaffType, label: "Zelador" },
      { value: "jardineiro_piscineiro" as StaffType, label: "Jardineiro/Piscineiro" },
      { value: "auxiliar_servicos_gerais" as StaffType, label: "Auxiliar de Serviços Gerais" }
    ]
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="bg-ita-blue hover:bg-ita-blue/90">
          <Plus className="h-4 w-4 mr-2" />
          Novo Colaborador
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-6">
          <DialogTitle className="text-xl font-semibold">Cadastrar Novo Colaborador</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Dados Pessoais */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
              Dados Pessoais
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="full_name">Nome Completo *</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                  placeholder="Digite o nome completo"
                  className="h-11"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cpf">CPF</Label>
                <Input
                  id="cpf"
                  value={formData.cpf}
                  onChange={(e) => handleCpfChange(e.target.value)}
                  placeholder="000.000.000-00"
                  maxLength={14}
                  className="h-11"
                />
              </div>
            </div>
          </div>

          {/* Função e Jornada */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
              Função e Jornada
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="staff_type">Função *</Label>
                <Select
                  value={formData.staff_type}
                  onValueChange={(value: StaffType) => setFormData(prev => ({ ...prev, staff_type: value }))}
                  required
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Selecione a função" />
                  </SelectTrigger>
                  <SelectContent>
                    {getStaffTypeOptions().map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {(isPontoEletronico || condominium?.type === 'controle_acessos') && (
                <div className="space-y-2">
                  <Label htmlFor="workload_hours">Jornada de Trabalho (horas) *</Label>
                  <Select
                    value={formData.workload_hours.toString()}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, workload_hours: parseInt(value) }))}
                    required
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Selecione a jornada" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="8">8 horas</SelectItem>
                      <SelectItem value="12">12 horas</SelectItem>
                      <SelectItem value="24">24 horas</SelectItem>
                      <SelectItem value="40">40 horas semanais</SelectItem>
                      <SelectItem value="44">44 horas semanais</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* Foto */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
              Foto do Colaborador
            </h3>
            <div className="flex justify-center">
              <PhotoCapture
                onPhotoSelected={(url) => setFormData(prev => ({ ...prev, photo_url: url }))}
                currentPhoto={formData.photo_url}
              />
            </div>
          </div>

          {/* Contato */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
              Informações de Contato
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Digite o e-mail"
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Telefone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Digite o telefone"
                  className="h-11"
                />
              </div>
            </div>
          </div>

          {/* Acesso ao Sistema */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
              Acesso ao Sistema
            </h3>
            <div className="max-w-md space-y-3">
              <Label htmlFor="password">Senha para Login</Label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Digite ou gere uma senha"
                    className="h-11 pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-muted"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGeneratePassword}
                  className="h-11 px-4 whitespace-nowrap flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Gerar
                </Button>
              </div>
              {generatedPassword && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">💡 Anote esta senha:</p>
                  <p className="text-sm font-mono font-semibold text-foreground">
                    {generatedPassword}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
              className="h-11 px-6"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={createStaffMember.isPending || !formData.full_name || !formData.staff_type}
              className="h-11 px-6 bg-ita-blue hover:bg-ita-blue/90"
            >
              {createStaffMember.isPending ? "Cadastrando..." : "Cadastrar Colaborador"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default NovoColaboradorModal
